/**
 * Provides lazy loading functionality for images to improve page performance and user experience.
 *
 * This class implements intersection-based lazy loading for elements with the `.lazy-load` class.
 * Images are loaded only when they enter the viewport, reducing initial page load time and bandwidth usage.
 * Includes loading states, error handling, and automatic cleanup of loaded elements.
 *
 * @remarks
 * - Monitors elements with `.lazy-load` class and `data-src` attributes
 * - Uses viewport detection to trigger image loading
 * - Displays loading spinner during image fetch
 * - Shows error state if image fails to load
 * - Automatically removes loaded elements from monitoring
 * - Listens to scroll and resize events for viewport detection
 *
 * @example
 * // Expected HTML structure:
 * // <div class="lazy-load" data-src="path/to/image.jpg"></div>
 *
 * // Initialization:
 * const lazyLoader = new LazyLoad({
 *   thumbnailLoadingHtml: '<div class="loading">Loading...</div>',
 *   thumbnailErrorHtml: '<div class="error">Failed to load</div>'
 * });
 *
 * @public
 */
export class LazyLoad {
  options: any = {};

  lazyImages: HTMLDivElement[] = []

  /**
   * Creates a new LazyLoad instance with specified loading and error templates.
   *
   * @param opts - Configuration object for lazy loading behavior
   * @param opts.thumbnailLoadingHtml - HTML content to display while image is loading
   * @param opts.thumbnailErrorHtml - HTML content to display if image fails to load
   */
  constructor(opts: any) {
    let defaults = {
      thumbnailLoadingHtml: `<i class="fa fa-spinner fa-3x fa-spin thumbnail" aria-hidden="true"></i><span class="filename">Loading preview</span>`,
      thumbnailErrorHtml: `<i class="text-warning fa fa-3x fa-exclamation-circle thumbnail" aria-hidden="true"></i>`
    };

    this.options = { ...defaults, ...opts };

    this.initialize();
  }

  /**
   * Initializes the lazy loading system by finding all lazy-load elements and setting up event listeners.
   *
   * @remarks Sets up scroll and resize event listeners for viewport detection
   * @private
   */
  initialize = () => {
    this.lazyImages = Array.prototype.slice.call(document.querySelectorAll(".lazy-load"));
    //console.log(`LazyLoad: Found ${this.lazyImages.length} lazy-load elements.`);
    if (this.lazyImages.length > 0) {
      this.lazyLoad();
      window.addEventListener("scroll", this.lazyLoad);
      window.addEventListener("resize", this.lazyLoad);
    }
  }

  lazyLoad = () => {
    this.lazyImages.forEach((div: HTMLDivElement) => {
      if (!div.classList.contains("lazy-loaded")) {
        if (div.dataset.src) {
          const src = div.dataset.src;
          //div.src = div.dataset.src;
          //div.classList.remove("lazy-load");
          //div.removeAttribute("data-src");
          div.innerHTML = this.options.thumbnailLoadingHtml;
          if (this.isInViewport(div)) {
            //console.log(`LazyLoad: Loading image from data-src: ${src}`);
            let isLoaded = false;
            let _img = document.querySelector(`img[src="src"]`) as HTMLImageElement | null;
            if (_img) {
              isLoaded = true;
            } else {
              _img = document.createElement(`img`) as HTMLImageElement;
              _img.src = src;
            }
            if (_img.complete) {
              this.setLazyBg(div, src);
            } else {
              _img.addEventListener('load', () => { this.setLazyBg(div, src, false); });
              _img.addEventListener('error', () => { this.setLazyBg(div, src, true) })
            }
            div.classList.add("lazy-loaded");
          } else {
            //console.log(`LazyLoad: Element not in viewport, skipping load for ${src}`);
          }
        }
      }

    });
    this.cleanLazy();
  }

  setLazyBg = (el: HTMLDivElement, src: string, error: boolean = false) => {
    if (error) {
      el.innerHTML = this.options.thumbnailErrorHtml;
    } else {
      el.innerHTML = "";
      el.style.backgroundImage = `url('${src}')`;
      //el.classList.add("lazy-loaded");
    }
  }

  cleanLazy = () => {
    this.lazyImages = Array.prototype.filter.call(this.lazyImages, (el: HTMLDivElement) => {
      return el.dataset.src;
    });
  }

  /**
   * Determines if an element is currently visible within the viewport.
   *
   * @param el - The HTML element to check
   * @returns True if the element is completely within the viewport bounds
   * @private
   */
  isInViewport = (el: HTMLElement): boolean => {
    const rect = el.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  }
}